// The DOM-facing UI: the console + on-screen chat feed, and the lobby
// (join form and room/mode pickers). Kept out of main.js so that file
// reads as the game loop and input handling, not a pile of
// getElementById. Both talk to the network through a Client instance
// (net.js) rather than owning a socket themselves.

import { createConsole } from "./console.js";
import { enabledOptionals, autoEnabled, configurablePlugins, fieldDefault, collectPluginConfig, PRESETS, presetPicks, orderPlugins } from "./wizard.js";

const CHAT_TTL = 8; // seconds a feed line stays on screen after it arrives
const CHAT_MAX = 6; // most feed lines shown at once

// The console, plus feed(): anything worth reading twice goes to the
// console (the scrollback log) AND flashes on screen (the
// notification). The flash list is aged by the caller each frame
// (ageChat) and read into the HUD.
export function createConsolePanel(hooks) {
    const con = createConsole(hooks);
    const chatFeed = [];

    function feed(text, kind = "chat") {
        con.print(text, kind);
        chatFeed.push({ text, kind, ttl: CHAT_TTL });
        while (chatFeed.length > CHAT_MAX) chatFeed.shift();
    }

    function ageChat(dt) {
        for (let i = chatFeed.length - 1; i >= 0; i--) {
            chatFeed[i].ttl -= dt;
            if (chatFeed[i].ttl <= 0) chatFeed.splice(i, 1);
        }
    }

    return { con, feed, ageChat, chatFeed };
}

// The lobby: the join form and the live room/mode pickers, over plain
// HTTP before any WebSocket exists, so you can see what a server runs
// — and how busy — before committing. `join()` (from main) reads the
// form and opens the connection; this owns everything up to that.
// Selection state lives on the Client (client.roomList /
// client.pickedRoom) so a `welcome` and the lobby agree on what's
// picked.
export function createLobby({ client, join }) {
    const overlay = document.getElementById("overlay");
    let modeList = [];   // the game PRESETS (Set 20.1) — { id, label, blurb, base, bundle, fragLimit, timeLimit, generator }
    let genList = [];    // world generators the server offers
    let pluginList = []; // EVERY plugin, each a toggle — { id, label, blurb, config, needs, core }
    let coreList = [];   // legacy: always-on plugins with options (older servers); unused when presets drive the form
    let coreNames = [];  // the base bundle a preset with base:true includes, for resolving its pre-checked set
    let canCreate = true;
    let lobbyError = null;

    // Create-a-game WIZARD state (Set 12.5): which step is showing, which optional plugins the player
    // ticked (dependencies are closed over these by wizard.js, not stored), and the chosen option values
    // keyed "pluginId/fieldId". Reset each time the form opens.
    let step = 0;
    const picked = new Set();
    const optValues = {};
    let plugSearch = "";       // the Plugins-step search filter (Set 12.7)
    let plugEnabledLast = false; // sort enabled plugins to the end

    const serverBase = () => {
        const host = (document.getElementById("server").value || "").trim();
        return host ? (host.startsWith("http") ? host : `http://${host}`) : "";
    };

    async function loadRooms() {
        try {
            // no-store: the lobby must reflect the server RIGHT NOW — its live rooms, and the plugins
            // it currently offers. A cached /api/rooms once showed a stale plugin list (only the older
            // optional set) after the server had gained more.
            const res = await fetch(`${serverBase()}/api/rooms`, { cache: "no-store" });
            // A server that predates rooms has no /api/rooms and
            // refuses it as a static path. Saying which status came
            // back beats "couldn't reach that server" — the server is
            // right there, it just isn't the one you think it is.
            if (!res.ok) throw new Error(`this server answered ${res.status} — is it an older build? (restart it)`);
            const body = await res.json();
            client.roomList = body.rooms || [];
            modeList = body.presets || body.modes || []; // presets are the game bundles now; modes = old servers
            genList = body.generators || [];
            pluginList = body.plugins || [];
            coreList = body.corePlugins || [];
            coreNames = body.core || [];
            canCreate = body.canCreate !== false && modeList.length > 0;
            lobbyError = null;
        } catch (e) {
            // Fail loudly and consistently: no rooms, no modes, no
            // create button. An empty dropdown behind a live button
            // is worse than an error, because it looks like the
            // server offers nothing rather than like something broke.
            client.roomList = [];
            modeList = [];
            canCreate = false;
            lobbyError = e instanceof TypeError ? "couldn't reach that server" : e.message;
        }
        if (client.pickedRoom == null || !client.roomList.some((r) => r.id === client.pickedRoom)) {
            client.pickedRoom = (client.roomList.find((r) => r.players < r.maxPlayers) || client.roomList[0] || {}).id ?? null;
        }
        renderRooms();
        renderModes();
    }

    // Text goes in with textContent, never innerHTML: room names are
    // typed by strangers now, and a name is a string, not markup.
    function renderRooms() {
        const el = document.getElementById("rooms");
        el.replaceChildren();
        if (lobbyError) {
            const div = document.createElement("div");
            div.className = "room full";
            const nm = document.createElement("span");
            nm.className = "rname";
            nm.textContent = lobbyError;
            div.append(nm);
            el.append(div);
        }
        for (const r of client.roomList) {
            const full = r.players >= r.maxPlayers;
            const div = document.createElement("div");
            div.className = `room${r.id === client.pickedRoom ? " on" : ""}${full ? " full" : ""}`;
            div.title = r.blurb || "";

            const left = document.createElement("span");
            const nm = document.createElement("span");
            nm.className = "rname";
            nm.textContent = r.name;
            const md = document.createElement("span");
            md.className = "rmode";
            md.textContent = r.modeLabel + (r.temporary ? " · player-made" : "");
            left.append(nm, document.createElement("br"), md);

            const count = document.createElement("span");
            count.className = "rcount";
            count.textContent = `${r.players}/${r.maxPlayers}`;

            div.append(left, count);
            if (!full) div.addEventListener("click", () => { client.pickedRoom = r.id; renderRooms(); });
            el.append(div);
        }
        const btn = document.getElementById("newgame");
        btn.disabled = !canCreate;
        btn.textContent = !modeList.length ? "can't make games on this server"
            : canCreate ? "+ New game"
            : "server is at its game limit";
        // Nothing to create with means nothing to show — don't leave
        // a half-built form open over an empty dropdown.
        if (!canCreate) toggleCreate(false);
    }

    // The mode dropdown is built from what the server said it knows,
    // so a server with a mode this client has never heard of still
    // works.
    function renderModes() {
        const sel = document.getElementById("cMode");
        const same = sel.options.length === modeList.length &&
            [...sel.options].every((o, i) => o.value === modeList[i].id);
        if (same) return; // don't clobber the player's selection on every poll
        sel.replaceChildren();
        for (const m of modeList) {
            const o = document.createElement("option");
            o.value = m.id;
            o.textContent = m.label;
            sel.append(o);
        }
        renderGen();
        applyPreset();
    }

    // Step 1 of the wizard, part A: the world-generator dropdown. Built from what the server offers,
    // so a new generator appears with no client change.
    function renderGen() {
        const gen = document.getElementById("cGen");
        if (gen && gen.options.length !== genList.length + 1) {
            gen.replaceChildren();
            const dflt = document.createElement("option");
            dflt.value = ""; dflt.textContent = "Default";
            gen.append(dflt);
            for (const g of genList) {
                const o = document.createElement("option");
                o.value = g; o.textContent = g.replace(/\b\w/g, (c) => c.toUpperCase());
                gen.append(o);
            }
        }
    }

    // Step 2, presets: canned plugin selections (Set 12.7) — clicking one replaces your picks with its
    // set (deps still close). A lightweight precursor to the "bases" idea. Built once from PRESETS.
    function renderPresets() {
        const row = document.getElementById("cPresets");
        if (!row || row.childElementCount) return;
        for (const pr of PRESETS) {
            const b = document.createElement("button");
            b.type = "button"; b.className = "preset"; b.dataset.preset = pr.id;
            b.textContent = `${pr.emoji} ${pr.label}`;
            b.addEventListener("click", () => {
                picked.clear();
                for (const id of presetPicks(pr, pluginList)) picked.add(id);
                renderPlugins();
            });
            row.append(b);
        }
    }

    // Step 2: the optional-plugin toggles, dependency-aware. Each row is a fixed grid (checkbox · name ·
    // note) so checkboxes line up no matter the note. A plugin ticked directly is a normal checkbox; one
    // pulled in by a dependency shows ticked-and-locked with a "needed by …" note (can't be turned off
    // from under its dependent). The list is filtered by the search box and, when sorting is on, enabled
    // plugins sink to the end. Rebuilt on every change so the dependency picture stays live.
    function renderPlugins() {
        const box = document.getElementById("cPlugins");
        if (!box) return;
        const enabled = enabledOptionals(pluginList, picked);
        const auto = autoEnabled(pluginList, picked);
        const list = orderPlugins(pluginList, enabled, { search: plugSearch, enabledLast: plugEnabledLast });
        box.replaceChildren();
        if (!list.length) {
            const none = document.createElement("div");
            none.className = "wiz-hint"; none.textContent = "No plugins match that search.";
            box.append(none); return;
        }
        for (const p of list) {
            const on = enabled.has(p.id);
            const pulledIn = on && !picked.has(p.id); // enabled only because something else needs it
            const lab = document.createElement("label");
            lab.className = "ctoggle" + (pulledIn ? " locked" : "") + (on ? " on" : "");
            lab.title = p.blurb || "";
            const cb = document.createElement("input");
            cb.type = "checkbox"; cb.value = p.id; cb.checked = on; cb.disabled = pulledIn;
            cb.addEventListener("change", () => { if (cb.checked) picked.add(p.id); else picked.delete(p.id); renderPlugins(); });
            const name = document.createElement("span");
            name.className = "cplug-name"; name.textContent = p.label;
            const note = document.createElement("span");
            note.className = "cplug-note";
            if (pulledIn) { note.classList.add("why"); note.textContent = `needed by ${auto[p.id].join(", ")}`; }
            else if (p.needs && p.needs.length) note.textContent = `+ ${p.needs.map((d) => (pluginList.find((x) => x.id === d) || {}).label || d).join(", ")}`;
            lab.append(cb, name, note);
            box.append(lab);
        }
    }

    // Step 3: an options control per field for every plugin that has one and is on — always-on core
    // plugins (jetpack, …) plus the enabled optionals that declare fields. choice -> a dropdown, multi
    // -> a checkbox set, string -> a text box. Values live in `optValues` keyed "pluginId/fieldId".
    function renderOptions() {
        const box = document.getElementById("cOptions");
        if (!box) return;
        const enabled = enabledOptionals(pluginList, picked);
        const list = configurablePlugins(pluginList, [], enabled); // every plugin's a toggle now; options come from whatever's enabled
        box.replaceChildren();
        if (!list.length) {
            const none = document.createElement("div");
            none.className = "wiz-hint";
            none.textContent = "Nothing to configure — the plugins you picked have no options.";
            box.append(none);
            return;
        }
        for (const p of list) {
            const sec = document.createElement("div");
            sec.className = "copt-plugin";
            const h = document.createElement("div");
            h.className = "copt-head";
            h.textContent = p.label + (p.always ? " · always on" : "");
            sec.append(h);
            for (const f of p.fields) {
                const key = `${p.id}/${f.id}`;
                if (!(key in optValues)) optValues[key] = fieldDefault(f);
                const row = document.createElement("div");
                row.className = "copt-row";
                const lab = document.createElement("span");
                lab.className = "copt-label";
                lab.textContent = f.label || f.id;
                row.append(lab);
                row.append(optionControl(f, key));
                sec.append(row);
            }
            box.append(sec);
        }
    }

    // The input for one config field, wired to write back into optValues[key].
    function optionControl(f, key) {
        if (f.type === "choice") {
            const sel = document.createElement("select");
            sel.className = "copt-input";
            for (const o of f.options || []) {
                const opt = document.createElement("option");
                opt.value = o; opt.textContent = o; if (o === optValues[key]) opt.selected = true;
                sel.append(opt);
            }
            sel.addEventListener("change", () => { optValues[key] = sel.value; });
            return sel;
        }
        if (f.type === "multi") {
            const wrap = document.createElement("div");
            wrap.className = "copt-multi";
            const chosen = new Set(Array.isArray(optValues[key]) ? optValues[key] : []);
            for (const o of f.options || []) {
                const l = document.createElement("label");
                l.className = "ctoggle";
                const cb = document.createElement("input");
                cb.type = "checkbox"; cb.value = o; cb.checked = chosen.has(o);
                cb.addEventListener("change", () => { if (cb.checked) chosen.add(o); else chosen.delete(o); optValues[key] = [...chosen]; });
                const s = document.createElement("span"); s.textContent = o;
                l.append(cb, s); wrap.append(l);
            }
            return wrap;
        }
        const inp = document.createElement("input"); // string
        inp.className = "copt-input"; inp.type = "text"; inp.maxLength = 200; inp.value = optValues[key] ?? "";
        inp.addEventListener("input", () => { optValues[key] = inp.value; });
        return inp;
    }

    // Show wizard page `n` (0 basics, 1 plugins, 2 options), (re)rendering the page's live content and
    // flipping the Back / Next / Create buttons for where we are.
    // A step counts as valid if its required fields are filled — only step 0 (Game) requires anything
    // (a name). `flagged` holds steps the player moved past while invalid; those show RED (Set 12.8).
    const flagged = new Set();
    function stepValid(n) { return n !== 0 || document.getElementById("cName").value.trim().length > 0; }
    function markSteps() {
        for (const it of document.querySelectorAll("#wizModal .wiz-navitem")) it.classList.toggle("bad", flagged.has(Number(it.dataset.step)));
        for (const sg of document.querySelectorAll("#wizModal .wiz-seg")) sg.classList.toggle("bad", flagged.has(Number(sg.dataset.step)));
    }

    function goStep(n) {
        const target = Math.max(0, Math.min(2, n));
        if (target !== step && !stepValid(step)) flagged.add(step); // moved past a step with bad fields
        step = target;
        for (const s of [...flagged]) if (stepValid(s)) flagged.delete(s); // clear any since-fixed
        if (step === 1) { renderPresets(); renderPlugins(); }
        if (step === 2) renderOptions();
        for (const pg of document.querySelectorAll("#wizModal .wiz-page")) pg.classList.toggle("hidden", Number(pg.dataset.page) !== step);
        for (const it of document.querySelectorAll("#wizModal .wiz-navitem")) it.classList.toggle("on", Number(it.dataset.step) === step);
        // The top bar fills segment by segment: past steps solid, the current one highlighted.
        for (const sg of document.querySelectorAll("#wizModal .wiz-seg")) {
            const s = Number(sg.dataset.step);
            sg.classList.toggle("done", s < step);
            sg.classList.toggle("on", s === step);
        }
        markSteps();
        document.getElementById("cBack").classList.toggle("hidden", step === 0);
        document.getElementById("cNext").classList.toggle("hidden", step === 2);
        document.getElementById("cCreate").classList.toggle("hidden", step !== 2);
        document.getElementById("cErr").textContent = "";
    }

    // Picking a preset pre-fills the whole form (Set 20.1): its recommended plugin BUNDLE becomes the
    // ticked set — (base ? the base game : nothing) + the preset's own plugins — and its generator is
    // selected. From there every plugin is a toggle the player can add or remove, so a preset is a
    // starting point, not a cage. Dependencies still close over the ticks (wizard.js).
    function applyPreset() {
        const p = modeList.find((x) => x.id === document.getElementById("cMode").value);
        if (p && Array.isArray(p.bundle)) {
            picked.clear();
            const base = p.base !== false ? coreNames : [];
            for (const id of [...base, ...p.bundle]) picked.add(id);
            const gen = document.getElementById("cGen");
            if (gen && p.generator !== undefined) gen.value = p.generator || "";
        }
        showModeBlurb();
        renderPlugins();
        renderOptions();
    }

    function showModeBlurb() {
        const m = modeList.find((x) => x.id === document.getElementById("cMode").value);
        document.getElementById("cBlurb").textContent = m ? m.blurb : "";
        // Show the mode's own limits as placeholders, so leaving a
        // field blank visibly means "whatever this mode normally
        // does" rather than "nothing".
        if (m) {
            document.getElementById("cFrag").placeholder = m.fragLimit ? `default ${m.fragLimit}` : "default: none";
            document.getElementById("cTime").placeholder = m.timeLimit ? `default ${Math.round(m.timeLimit / 60)}` : "default: none";
        }
    }

    async function createRoom() {
        const err = document.getElementById("cErr");
        err.textContent = "";
        // Don't POST an obviously invalid form: flag the Game step red and send the player back to fix it.
        if (!stepValid(0)) { flagged.add(0); goStep(0); document.getElementById("cErr").textContent = "name is required"; document.getElementById("cName").focus(); return; }
        const time = document.getElementById("cTime").value;
        const body = {
            name: document.getElementById("cName").value,
            mode: document.getElementById("cMode").value,
            maxPlayers: document.getElementById("cMax").value,
            fragLimit: document.getElementById("cFrag").value,
            // The form asks for minutes because that's how people
            // think about a match; the server counts seconds.
            timeLimit: time === "" ? "" : String(Math.round(Number(time) * 60)),
            // Sandbox: chosen world generator ("" = mode default), the dependency-closed plugin set,
            // and the per-plugin options (only values that differ from a plugin's declared default).
            generator: document.getElementById("cGen").value,
            // EXCLUSIVE (Set 20.1): the wizard sends the COMPLETE, dependency-closed plugin selection, so
            // the room loads exactly it — core plugins included only if ticked. Unticking everything makes
            // a bare room with no game logic.
            plugins: [...enabledOptionals(pluginList, picked)],
            exclusive: true,
            pluginConfig: collectPluginConfig(configurablePlugins(pluginList, [], enabledOptionals(pluginList, picked)), optValues),
            // Persistent worlds (Set 13): keep this room's terrain across restarts, exempt from reaping.
            persistent: document.getElementById("cPersist").checked,
            // The two combat settings (Set 15) — independent, and sent explicitly so the room takes
            // what the form shows rather than the mode's default.
            friendlyFire: document.getElementById("cFF").checked,
            selfDamage: document.getElementById("cSelf").checked,
        };
        let res, out;
        try {
            res = await fetch(`${serverBase()}/api/rooms`, {
                method: "POST",
                headers: { "content-type": "application/json" },
                body: JSON.stringify(body),
            });
            out = await res.json();
        } catch (e) {
            err.textContent = "couldn't reach that server";
            return;
        }
        if (!res.ok) {
            err.textContent = out.problems ? out.problems.join(" · ") : out.error || "couldn't create that game";
            return;
        }
        // Made it: select it and go. Creating a game you then have to
        // find in a list is a chore, so this joins straight away.
        client.pickedRoom = out.room.id;
        await loadRooms();
        toggleCreate(false);
        join();
    }

    function toggleCreate(on) {
        document.getElementById("wizModal").classList.toggle("hidden", !on);
        if (on) { flagged.clear(); goStep(0); document.getElementById("cName").focus(); }
    }

    // Menu side-tabs (Set 12.8): Rooms / Player / Settings. Pure chrome — swaps which pane shows.
    for (const t of document.querySelectorAll(".menu-tab")) t.addEventListener("click", () => {
        for (const x of document.querySelectorAll(".menu-tab")) x.classList.toggle("on", x === t);
        for (const p of document.querySelectorAll(".menu-pane")) p.classList.toggle("hidden", p.dataset.pane !== t.dataset.tab);
    });

    document.getElementById("play").addEventListener("click", join);
    document.getElementById("server").addEventListener("change", loadRooms);
    document.getElementById("newgame").addEventListener("click", () => toggleCreate(true));
    document.getElementById("cCancel").addEventListener("click", () => toggleCreate(false));
    document.getElementById("cBack").addEventListener("click", () => goStep(step - 1));
    document.getElementById("cNext").addEventListener("click", () => goStep(step + 1));
    document.getElementById("cCreate").addEventListener("click", createRoom);
    document.getElementById("cMode").addEventListener("change", applyPreset);
    document.getElementById("cName").addEventListener("keydown", (e) => { if (e.key === "Enter") goStep(step + 1); });
    // Typing a name clears the Game step's red flag as soon as it's valid.
    document.getElementById("cName").addEventListener("input", () => { if (stepValid(0)) { flagged.delete(0); markSteps(); } });
    // Jump straight to a step by clicking its icon in the left nav.
    for (const it of document.querySelectorAll("#wizModal .wiz-navitem")) it.addEventListener("click", () => goStep(Number(it.dataset.step)));
    // Plugins step: live search + a sort toggle that sinks enabled plugins to the end.
    document.getElementById("cPlugSearch").addEventListener("input", (e) => { plugSearch = e.target.value; renderPlugins(); });
    document.getElementById("cPlugSort").addEventListener("click", () => {
        plugEnabledLast = !plugEnabledLast;
        document.getElementById("cPlugSort").textContent = plugEnabledLast ? "Sort: enabled last" : "Sort: default";
        renderPlugins();
    });

    loadRooms();
    // The lobby is live: rooms fill up and player-made ones come and
    // go, so a panel left open shouldn't be showing a list from five
    // minutes ago.
    setInterval(() => { if (!overlay.classList.contains("hidden")) loadRooms(); }, 5000);

    return { loadRooms };
}
